/**
 * Labstep
 *
 * @module rtk/services/organization-metric
 * @desc Query the API for organization metrics using RTK Query
 */

import {
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import type {
  ORGANIZATION_ANALYTICS_INTERVAL,
  ORGANIZATION_ANALYTICS_METRIC,
} from 'labstep-web/constants/analytics';
import type { OrganizationMetricData } from 'labstep-web/rtk/services/types';
import { configService } from 'labstep-web/services/config.service';
import { selectAuthenticatedUser } from 'labstep-web/state/selectors/authenticated-user';
import type { LabstepReduxState } from 'labstep-web/state/types';

export const organizationAnalyticsApi = createApi({
  reducerPath: 'organizationAnalyticsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: configService.labstepApiUrl,
    prepareHeaders: (headers, { getState }) => {
      const state = getState() as LabstepReduxState;
      const authenticatedUser = selectAuthenticatedUser(state);

      if (authenticatedUser?.token) {
        headers.set(
          'Authorization',
          `Bearer ${authenticatedUser.token}`,
        );
      }

      return headers;
    },
  }),
  tagTypes: ['OrganizationMetric'],
  endpoints: (builder) => ({
    getOrganizationMetric: builder.query<
      OrganizationMetricData,
      {
        interval: ORGANIZATION_ANALYTICS_INTERVAL;
        metric: ORGANIZATION_ANALYTICS_METRIC;
      }
    >({
      query: ({ interval, metric }) =>
        `api/generic/organization/analytics/${interval}/${metric}`,
      providesTags: (result, error, { interval, metric }) => [
        { type: 'OrganizationMetric', id: `${interval}_${metric}` },
      ],
      // Cache for 5 minutes (300 seconds) - typical for analytics data
      keepUnusedDataFor: 300,
    }),
  }),
});

export const { useGetOrganizationMetricQuery } =
  organizationAnalyticsApi;
